<script setup lang="ts">
import { computed, nextTick, ref, toRefs, watch, watchEffect } from 'vue'
import { TransitionRoot, TransitionChild, Dialog, DialogPanel, DialogTitle } from '@headlessui/vue'
import { useRoute, useRouter } from 'vue-router'
import { GetChatMessagesAndInfo } from '@/components/chat/graphql/chats.queries.ts'
import {
  FindProjects_WidgetAssistantPhotos_PlatformLanguages,
} from '@/components/projects/graphql/project.queries.ts'
import { provideApolloClient, useMutation, useQuery, useSubscription } from '@vue/apollo-composable'
import type { ChatMessage } from '@/components/chat/interfaces/chat-message.interfaces.ts'
import { CombinedGraphQLErrors } from '@apollo/client/core'
import dayjs from 'dayjs'
import { DAYS_IN_WEEK, USER_TYPING_MESSAGE_THROTTLE_DELAY_MS } from '@/config/config.ts'
import type { ChatWSMessage } from '@/components/chat/interfaces/chat.interfaces.ts'
import * as I from '@/components/chat/interfaces/chat.interfaces.ts'
import * as IProjects from '@/components/projects/interfaces.ts'
import { cloneDeep, isEmpty, isUndefined, throttle } from 'lodash'
import { getDefaultApolloClient } from '@/graphql.ts'
import { ChatEvent } from '@/enums.ts'
import { StopCircleIcon } from '@heroicons/vue/24/solid'
import type { StatusResponse } from '@/components/users/interfaces.ts'
import {
  ChatIamTypingMessageMutation,
  ChatSendMessageMutation,
} from '@/components/chat/graphql/mutations.graphql.ts'
import { useTranslations } from '@/composables/useTranslations.ts'
import { isNumeric } from '@/utils/util.ts'
import { sanitizeHtml } from '@/utils/sanitize.ts'
import { useToast } from 'vue-toastification'
import isToday from 'dayjs/plugin/isToday'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { UserIcon } from '@heroicons/vue/20/solid'
import { faAngleRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { API_BASE_URL } from '@/config/env.const.ts'
import { useUserStore } from '@/stores/user.store.ts'
import { storeToRefs } from 'pinia'
import { GET_CHAT_MESSAGES_SUBSCRIPTION } from '@/components/chat/graphql/subscriptions.graphql.ts'

dayjs.extend(isToday)
dayjs.extend(customParseFormat)
provideApolloClient(getDefaultApolloClient())

const props = defineProps<{ chatIndex: number; projectId: number }>()
const { t } = useTranslations()

const toast = useToast()
const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const { hasPresentationModeEnabled } = storeToRefs(userStore)
const currentDayNumber = Number(dayjs().format('DD'))
const isModalOpen = ref(false)
const messages = ref<ChatMessage[]>([])
const trackingInfo = ref<I.ChatTrackingInfo | null>(null)
const scrollableDivRef = ref<HTMLDivElement | null>(null)
const messagesRefs = ref<HTMLDivElement[]>([])
const message = ref<string>('')
const chatAssistantPhotoUrl = ref<string>('')
const emit = defineEmits(['onStopAiClicked'])
// Switch case?
const messageStyles = {
  [ChatEvent.Customer]: {
    alignment: 'justify-start flex items-start',
    bubbleClass: 'bg-[#7B66FF] text-white rounded-t-2xl rounded-br-2xl',
    timeAlignment: 'justify-start',
  },
  [ChatEvent.AI]: {
    alignment: 'justify-end flex items-end',
    bubbleClass: 'bg-white text-black border-2 border-solid rounded-t-2xl rounded-bl-2xl',
    timeAlignment: 'justify-end',
  },
  [ChatEvent.CustomerSupport]: {
    alignment: 'justify-end flex items-end',
    bubbleClass: 'bg-[rgba(136,77,223,0.78)] text-white rounded-t-2xl rounded-bl-2xl',
    timeAlignment: 'justify-end',
  },
  //TODO: Remove this when we have a better solution for the AI control
  [ChatEvent.ControlHasBeenInterceptedByCustomerSupport]: {
    alignment: 'justify-center',
    bubbleClass: 'bg-gray-200 text-gray-600 rounded-2xl',
    timeAlignment: 'display: none',
  },
}
// Computed
const isSendMessageButtonDisabled = computed(() => {
  return message.value.trim().length === 0 || !isAiControlIntercepted.value
})
const isAiControlIntercepted = computed<boolean>(() => {
  return !isUndefined(
    messages.value.find(({ event }) => {
      return event === ChatEvent.ControlHasBeenInterceptedByCustomerSupport
    }),
  )
})
const gqlInfoAndMessagesVariables = ref<{
  chatIndex: number
  projectId: number
}>({
  chatIndex: props.chatIndex,
  projectId: props.projectId,
})
const gqlVariables = computed(() => ({
  input: { projectIds: [props.projectId] },
  projectId: props.projectId,
}))
const gradientStyle = computed(() => {
  const gradients = widgetVisual.value?.gradients
  const background = widgetVisual.value?.color

  if (!gradients?.enabled || !gradients?.colors || gradients.colors.length < 2) {
    return { background }
  }

  const gradient = `linear-gradient(${gradients.degree}deg, ${gradients.colors.join(', ')})`

  return { background: gradient }
})

// Functions
function setMessages(items: ChatMessage[]) {
  messages.value = items
}

function setChatTrackingInfo(info: I.ChatTrackingInfo) {
  trackingInfo.value = info
}

function toHumanReadableTimeOrDateIfSomeTimeGone(time: string): string {
  const instance = dayjs(time, 'YYYY-MM-DDTHH:mm:ss.000ZZ')
  const instanceDay = Number(instance.format('DD'))

  if (instance.isToday()) {
    return instance.format('HH:mm')
  } else if (Math.abs(instanceDay - currentDayNumber) < DAYS_IN_WEEK) {
    return instance.format('dddd DD.MM HH:mm')
  }

  return instance.format('dddd DD.MM.YYYY HH:mm')
}

async function onNewMessagesReceived(msgs: ChatWSMessage[]) {
  console.log('onNewMessagesReceived:', msgs)
  for (const message of msgs) {
    const isMessageForCurrentChat = Number(route.params.chatIndex) === message.chatIndex

    if (!isMessageForCurrentChat) {
      console.warn('Message not for current chat, skipping!', message)
      continue
    }

    messages.value.push({
      eventId: message.id,
      event: message.event, // TODO: Enum maybe?
      content: message.content,
      createdAt: message.createdAt,
    })

    await nextTick()
    scrollChatToBottom()
  }
}

function scrollChatToBottom() {
  if (scrollableDivRef.value instanceof HTMLDivElement) {
    try {
      // const lastMessageIndex = messagesRefs.value.length - 1
      // const lastMessageRef = messagesRefs.value[lastMessageIndex]
      // const rects = lastMessageRef.getBoundingClientRect()

      scrollableDivRef.value.scrollTo({
        // TODO: Fix 2500
        top: scrollableDivRef.value.scrollHeight,
        behavior: 'smooth',
      })
    } catch (err) {
      console.error('ChatMessages | scrollChatToBottom | err:', err)
    }
  }
}

// GraphLQ Mutation:
const { onError: onIamTypingMutationError, mutate: publishIamTypingEvent } = useMutation<{
  status: StatusResponse
}>(ChatIamTypingMessageMutation)

function setMessage(value: string): void {
  message.value = value
}

onIamTypingMutationError((error) => {
  console.error('ChatMessages | publishIamTypingMessageMutation | Error:', error)
})

function publishIamTypingMessageMutation(projectId: number, chatIndex: number): void {
  publishIamTypingEvent({ variables: { projectId, chatIndex } })
}

const {
  onError: onSendMessageError,
  onDone: onSendMessageDone,
  mutate: onSendMessageMutation,
} = useMutation<{ status: StatusResponse }>(ChatSendMessageMutation)

function onSendMessageButtonPressed(): void {
  if (message.value.trim().length === 0) {
    console.warn('onSendMessageButtonPressed: Message is empty')
    return
  }

  onSendMessageMutation({
    variables: {
      input: {
        projectId: props.projectId,
        chatIndex: props.chatIndex,
        message: message.value,
      },
    },
  })

  onSendMessageDone((result) => {
    setMessage('')
    console.error('ChatMessages | onSendMessageButtonPressed | Done:', result)
  })

  onSendMessageError((error) => {
    console.error('ChatMessages | onSendMessageButtonPressed | Error:', error)
    toast.error(t('common.notifications.formErrorData'))
  })
}

const onUserTypingMessage = throttle(() => {
  // TODO: Don't send if key is enter
  publishIamTypingMessageMutation(
    props.projectId ?? -1,
    parseInt(route.params.chatIndex as string),
  )
}, USER_TYPING_MESSAGE_THROTTLE_DELAY_MS, { trailing: true })

function onTextareaKeydownEventCaptured(event: KeyboardEvent): void {
  if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
    event.preventDefault()
    onSendMessageButtonPressed()
  }
}

const { onResult: onResultChatAssistantPhotos } = useQuery<{ projects: IProjects.Project[] }>(
  FindProjects_WidgetAssistantPhotos_PlatformLanguages,
  { variables: gqlVariables, fetchPolicy: 'cache-and-network' },
)

const widgetVisual = ref<IProjects.IVisualSettings | null>(null)
const widgetAssistant = ref<IProjects.IAssistantSettings | null>(null)
onResultChatAssistantPhotos((result) => {
  const settings = result?.projects[0]?.settings

  if (settings) {
    setChatWidgetVisualSettings(settings.widgetVisual)
    // @ts-expect-error Jevgeni fix it later
    setChatWidgetAssistantSettings(settings.widgetAssistant)
  }
})

function setChatWidgetVisualSettings(widgetVisualSettings: IProjects.IVisualSettings) {
  widgetVisual.value = widgetVisualSettings

  const filePath = widgetVisualSettings?.avatarUrl
  chatAssistantPhotoUrl.value = filePath ? new URL(filePath, API_BASE_URL).toString() : ''
}

function setChatWidgetAssistantSettings(widgetAssistantSettings: IProjects.IAssistantSettings) {
  widgetAssistant.value = widgetAssistantSettings
}

const {
  onResult: onResultMessagesAndInfo,
  onError: onErrorChatMessagesAndInfo,
  refetch: refetchChatMessagesAndTrackingInfo,
} = useQuery<{
  messages: ChatMessage[]
  trackingInfo: I.ChatTrackingInfo
}>(
  GetChatMessagesAndInfo,
  { variables: gqlInfoAndMessagesVariables },
)

onResultMessagesAndInfo(async (result) => {
  if (!result?.messages || !result?.trackingInfo) {
    await router.replace({ name: 'not-found' })
    return
  }

  setMessages(cloneDeep(result.messages)) // TODO: Use GraphQL way
  setChatTrackingInfo(result.trackingInfo)

  await nextTick()

  scrollChatToBottom()
})

onErrorChatMessagesAndInfo((error) => {
  console.log('onError:', error)

  if (CombinedGraphQLErrors.is(error) && error.errors.some((e) => e.extensions?.code === 'NOT_FOUND')) {
    router.replace({ name: 'not-found' })
    return
  }

  toast.error(t('common.notifications.formErrorUrl'))
})

function getMessageSender(event: ChatEvent): string {
  switch (event) {
    case ChatEvent.AI:
      return widgetAssistant.value?.name ?? 'AI agent'
    case ChatEvent.CustomerSupport:
      return 'Support'
    case ChatEvent.Customer:
      return 'Customer'
    default:
      return ''
  }
}

function isCustomerMessage(event: ChatEvent): boolean {
  return event === ChatEvent.Customer
}

function closeModal() {
  isModalOpen.value = false
}
function openModal() {
  isModalOpen.value = true
}

function onStopAiButtonClicked() {
  closeModal()
  emit('onStopAiClicked')
}

// GraphQL: Chat messages subscriptions
const {
  result: newMessages,
  onError: onNewMessagesSubscriptionFailed
} = useSubscription<{ chatMessages: ChatWSMessage[] }>(GET_CHAT_MESSAGES_SUBSCRIPTION, { fetchPolicy: 'no-cache' })

watch(newMessages, (newMessagesValue) => {
  if (!newMessagesValue) {
    return
  }

  onNewMessagesReceived(newMessagesValue.chatMessages)
})

/* watchEffect(async() => {
  console.log('ChatMessagesV2 | watchEffect 1 |', 'props.chatIndex', props.chatIndex, 'props.projectId', props.projectId)

  if (isNumeric(props.chatIndex)) {
    // gqlInfoAndMessagesVariables.value.chatIndex = props.chatIndex
  }
  if (isNumeric(props.projectId)) {
    // gqlInfoAndMessagesVariables.value.projectId = props.projectId
  }

  console.log('ChatMessagesV2 | watchEffect 2 |', 'props.chatIndex', props.chatIndex, 'props.projectId', props.projectId)

  if (true || 5 > 6) {
    // await refetchChatMessagesAndTrackingInfo(gqlInfoAndMessagesVariables.value)
  }
}) */

const { chatIndex: chatIndexRef, projectId: projectIdRef } = toRefs<{
  chatIndex: number
  projectId: number
}>(props)

watch([chatIndexRef, projectIdRef], async ([newChatIndex, newProjectId]) => {
  if (isNumeric(newChatIndex))
    gqlInfoAndMessagesVariables.value.chatIndex = newChatIndex
  if (isNumeric(newProjectId))
    gqlInfoAndMessagesVariables.value.projectId = newProjectId

  await refetchChatMessagesAndTrackingInfo(gqlInfoAndMessagesVariables.value)
})

defineExpose({ trackingInfo, isAiControlIntercepted })
</script>

<template>
  <div
    class="grow md:h-[calc(100vh-13rem)] max-h-[1500px] max-w-[700px]:min-w-[500px] shadow-md rounded-lg p-2 pb-2"
    :style="gradientStyle"
  >
    <div class="flex flex-col grow max-h-[1480px] mx-auto">
      <div class="shadow-lg flex items-center h-8 bg-gray-100 rounded-lg mb-2">
        <div class="flex justify-around items-center text-sm md:text-md w-full">
          <div class="sm:hidden">
            <!-- Ai control modal -->
            <button
              :disabled="isAiControlIntercepted"
              @click="openModal"
              class="rounded-lg h-7 flex justify-center items-center w-auto px-3"
              :class="[
                isAiControlIntercepted
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-fuchsia-custom text-white hover:bg-red-400',
              ]"
            >
              <StopCircleIcon class="w-5 h-5" />
              <span>{{ t('chats.aiControlModal.stopAiControl') }}</span>
            </button>
            <TransitionRoot appear :show="isModalOpen" as="template">
              <Dialog as="div" @close="closeModal" class="relative z-200">
                <TransitionChild
                  as="template"
                  enter="duration-300 ease-out"
                  enter-from="opacity-0"
                  enter-to="opacity-100"
                  leave="duration-200 ease-in"
                  leave-from="opacity-100"
                  leave-to="opacity-0"
                >
                  <div class="fixed inset-0 bg-black/25" />
                </TransitionChild>

                <div class="fixed inset-0 overflow-y-auto">
                  <div class="flex min-h-full items-center justify-center p-4 text-center">
                    <TransitionChild
                      enter="duration-300 ease-out"
                      enter-from="opacity-0 scale-95"
                      enter-to="opacity-100 scale-100"
                      leave="duration-200 ease-in"
                      leave-from="opacity-100 scale-100"
                      leave-to="opacity-0 scale-95"
                    >
                      <DialogPanel
                        class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all"
                      >
                        <DialogTitle as="h3" class="text-lg font-medium leading-6 text-gray-900">
                          {{ t('chats.aiControlModal.title') }}
                        </DialogTitle>
                        <div class="mt-2">
                          <p class="text-sm text-gray-500">
                            {{ t('chats.aiControlModal.subtitle') }}
                          </p>
                        </div>

                        <div class="mt-4 flex justify-between items-center">
                          <button
                            type="button"
                            class="w-28 h-10 inline-flex justify-center rounded-md border border-transparent bg-fuchsia-custom px-4 py-2 text-sm font-medium text-white hover:bg-red-200 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                            @click="onStopAiButtonClicked"
                          >
                            {{ t('chats.aiControlModal.yes') }}
                          </button>
                          <button
                            type="button"
                            class="w-28 h-10 inline-flex justify-center rounded-md border border-transparent bg-purple-custom opacity-70 px-4 py-2 text-sm font-medium text-white hover:bg-blue-200 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                            @click="closeModal"
                          >
                            {{ t('chats.aiControlModal.no') }}
                          </button>
                        </div>
                      </DialogPanel>
                    </TransitionChild>
                  </div>
                </div>
              </Dialog>
            </TransitionRoot>
          </div>
          <span class="text-black">{{ t('chats.page.chat') }} #{{ props.chatIndex }}</span>
          <span class="text-black">
            {{ t('chats.page.aiControl') }}:
            {{ isAiControlIntercepted ? t('chats.page.disabled') : t('chats.page.enabled') }}
          </span>
        </div>
      </div>
      <div
        class="grow h-[calc(100vh-23rem)] bg-white shadow-lg rounded-t-xl p-4 overflow-y-auto"
        ref="scrollableDivRef"
      >
        <div v-for="(message, index) in messages" :key="index" ref="messagesRefs">
          <div
            v-if="message.event === ChatEvent.ControlHasBeenInterceptedByCustomerSupport"
            :key="`controlHasBeenInterceptedByCustomerSupport_${index}`"
            class="flex py-5 items-center"
          >
            <div class="grow border-t border-gray-400"></div>
            <div class="flex flex-col items-center mx-4 text-gray-400 max-sm:text-xs">
              <div class="text-gray-400 text-sm">
                {{ t('chats.page.controlHasBeenInterceptedByCustomerSupport') }}
              </div>
              <div :class="{
                'justify-center text-gray-400 text-xs': true,
                'blurry-text': hasPresentationModeEnabled
              }">
                {{ toHumanReadableTimeOrDateIfSomeTimeGone(message.createdAt) }}
              </div>
            </div>
            <div class="grow border-t border-gray-400"></div>
          </div>

          <div
            v-if="!isEmpty(message.content)"
            :class="{
              'flex flex-col py-2': true,
              [messageStyles[message.event]?.alignment ?? '']: true,

            }"
          >
            <div
              class="text-xs items-end px-16"
              :class="messageStyles[message.event]?.timeAlignment"
            >
              {{ toHumanReadableTimeOrDateIfSomeTimeGone(message.createdAt) }}
              <span :class="{ 'blurry-text': hasPresentationModeEnabled }">
                {{ getMessageSender(message.event) }}
              </span>
            </div>
            <div class="flex" :class="{ [isCustomerMessage(message.event) ? '' : 'flex-row-reverse']: true }">
              <div class="relative flex items-center justify-center h-full">
                <div
                  class="size-12 mx-1 bg-stone-400 rounded-full flex justify-center items-center"
                >
                  <UserIcon
                    v-if="isCustomerMessage(message.event)"
                    class="w-6 h-6 text-amber-100"
                    aria-hidden="true"
                  />
                  <div v-else class="text-purple-100">
                    <img alt="AI" :src="chatAssistantPhotoUrl" />
                  </div>
                </div>
              </div>
              <div
                class="max-sm:p-4 min-w-44 max-w-[70%] text-pretty max-sm:text-xs flex items-center p-3 break-words white-space-pre-wrap crowdy-the-message"
                :class="{
                  [messageStyles[message.event]?.bubbleClass ?? '']: true,
                  'blurry-text': hasPresentationModeEnabled
                }"
              >
                <span class="overflow-hidden" v-html="sanitizeHtml(message.content)"></span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- AI Control Intercepted Section -->
      <!-- Input Box -->
      <div class="relative shrink-0 p-4 rounded-b-xl bottom-0 bg-white -mt-1">
        <input
          ref="messageInputRef"
          v-model="message"
          @keydown="onUserTypingMessage"
          @keydown.enter="onTextareaKeydownEventCaptured"
          :disabled="!isAiControlIntercepted"
          name="comment"
          id="comment"
          class="w-full h-14 rounded-full px-3 py-1.5 focus:border-0 border border-gray-300 sm:text-sm pr-12"
          :class="{
            'bg-white text-base text-gray-900 placeholder:text-gray-400': isAiControlIntercepted,
            'bg-gray-200': !isAiControlIntercepted,
          }"
          type="text"
        />
        <button
          type="button"
          class="absolute size-12 top-11 right-5 transform -translate-y-1/2"
          :disabled="isSendMessageButtonDisabled"
          :class="{
            'rounded-full bg-indigo-500 text-white shadow-xs hover:bg-indigo-500 hover:cursor-pointer':
              !isSendMessageButtonDisabled,
            'rounded-full bg-stone-300 text-white shadow-xs hover:cursor-not-allowed':
              isSendMessageButtonDisabled,
          }"
          @click="onSendMessageButtonPressed"
        >
          <font-awesome-icon :icon="faAngleRight" class="size-6 items-center" aria-hidden="true" />
        </button>
      </div>
      <div class="text-center text-xs text-white pt-1">{{ t('chats.page.poweredByCrowdyAi') }}</div>
    </div>
  </div>
</template>
<style>
.crowdy-the-message a {
  color: pink
}
</style>
